import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import Globals from "../../utils/globals";

const ResolutionFormEditor = () => {
  const { getAccessTokenSilently } = useAuth0();

  const agencyId = useSelector(
    (state) => state.agencyStubs.selectedAgencyId
  );
  //   console.log(
  //   "[ResolutionFormEditor] agency redux:",
  //   agency
  // );

  // THESE ARE STATE ENTRIES
  // UI terminology: "fields"
  // Database terminology: these are stored in the schema's `items[]` array.
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Index of the field currently being edited.
  const [editingIndex, setEditingIndex] = useState(null);

  // Working copy of one Selection List option being edited.
  // Nothing changes in editingField.items[] until Save Option is clicked.
  const [editingOptionIndex, setEditingOptionIndex] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [removingOptionIndex, setRemovingOptionIndex] = useState(null);

  // Which FORM FIELD is currently asking for remove confirmation.
  const [removingFieldIndex, setRemovingFieldIndex] = useState(null);

  // Working copy of the field being edited.
  // Nothing in the real form changes until Save Field is clicked.
  const [editingField, setEditingField] = useState(null);

  // True while creating a brand-new Selection List option.
  const [isAddingOption, setIsAddingOption] = useState(false);
  const [isAddingField, setIsAddingField] = useState(false);
  const [newField, setNewField] = useState(null);

  // True when the working Resolution Form has changes
  // that have not yet been saved to the database.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const canLoad = !!agencyId;
  const apiBase = Globals.apiEndpoint;

  const load = async () => {
    if (!canLoad) return;

    setLoading(true);

    try {
      const token = await getAccessTokenSilently();

      const url =
        `${apiBase}/resolutions/agencies/${agencyId}` +
        `/clearance_schema`;

      const resp = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        alert(
          json?.message || "Failed to load the Resolution Form"
        );
        return;
      }

      const schema = json?.resolution_schema || null;
      // this 'items' refer the db field name it is using for the array..
      setFields(
        Array.isArray(schema?.items) ? schema.items : []
      );


      // Reset the editor state because we just loaded a fresh copy from Mongo.
      setHasUnsavedChanges(false);


      setEditingIndex(null);
    } catch (error) {
      console.error(
        "[ResolutionFormEditor] load failed:",
        error
      );

      alert("Network or token error loading the Resolution Form");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [agencyId]);


  // ------------------------------------------------------------
  // SELECTION LIST OPTION EDITOR
  // ------------------------------------------------------------

  const startOptionEdit = (option, optionIndex) => {
    setEditingOptionIndex(optionIndex);

    setEditingOption({
      ...option,

      // Copy title separately so the original option is not mutated.
      title: {
        ...(option?.title || {}),
      },
    });
  };

  const cancelOptionEdit = () => {
    setEditingOptionIndex(null);
    setEditingOption(null);
  };

  const saveOptionEdit = () => {
    if (
      editingOptionIndex === null ||
      !editingOption ||
      !editingField ||
      editingIndex === null
    ) {
      return;
    }

    // Update the selected option inside the field's working copy.
    const updatedOptions = editingField.items.map(
      (option, optionIndex) =>
        optionIndex === editingOptionIndex
          ? editingOption
          : option
    );

    const updatedField = {
      ...editingField,
      items: updatedOptions,
    };

    // Keep the open field editor current.
    setEditingField(updatedField);

    // IMPORTANT:
    // Save Form sends fields[] to the backend,
    // so the accepted option change must also be placed there.
    setFields(
      fields.map((field, index) =>
        index === editingIndex
          ? updatedField
          : field
      )
    );

    // Working form now differs from MongoDB.
    setHasUnsavedChanges(true);

    // Close the option editor.
    setEditingOptionIndex(null);
    setEditingOption(null);
  };

  if (!agencyId) {
    return (
      <div style={{ padding: 12 }}>
        <strong>No agency selected.</strong>
        <div>Select an agency first, then come back to Config.</div>
      </div>
    );
  }

  // ------------------------------------------------------------
  // FIELD EDITOR
  // ------------------------------------------------------------

  // Open the editor using a COPY of the selected field.
  const startFieldEdit = (field, index) => {
    setEditingIndex(index);

    setEditingField({
      ...field,

      // Copy title separately so we do not accidentally mutate
      // the original schema item's title object.
      title: {
        ...(field?.title || {}),
      },
    });
  };

  // Cancel editing and throw away the working copy.
  const cancelFieldEdit = () => {
    setEditingIndex(null);
    setEditingField(null);
  };

  // Save the working copy into LOCAL React state only.
  // This DOES NOT save anything to MongoDB yet.
  const saveFieldEdit = () => {
    if (editingIndex === null || !editingField) return;

    const cleanedEditingField = {
      ...editingField,
    };

    // Legacy-compatible behavior:
    // only store `required` when the field is actually required.
    // Missing `required` means false.
    if (cleanedEditingField.required === true) {
      cleanedEditingField.required = true;
    } else {
      delete cleanedEditingField.required;
    }

    const updatedFields = fields.map((field, index) =>
      index === editingIndex ? cleanedEditingField : field
    );

    setFields(updatedFields);
    setHasUnsavedChanges(true);

    setEditingIndex(null);
    setEditingField(null);
  };

  const saveForm = async () => {
    if (!agencyId || !hasUnsavedChanges) return;

    setSaving(true);

    try {
      const token = await getAccessTokenSilently();

      const url =
        `${apiBase}/resolutions/agencies/${agencyId}` +
        `/clearance_schema`;

      const resp = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fields,
        }),
      });

      const json = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        alert(
          json?.message ||
          "Failed to save the Resolution Form"
        );
        return;
      }

      const savedSchema = json?.resolution_schema || null;

      // Database uses schema.items[].
      // UI continues to call them fields[].
      if (Array.isArray(savedSchema?.items)) {
        setFields(savedSchema.items);
      }

      setHasUnsavedChanges(false);

      console.log(
        "[ResolutionFormEditor] Resolution Form saved:",
        savedSchema
      );
    } catch (error) {
      console.error(
        "[ResolutionFormEditor] save failed:",
        error
      );

      alert("Network or token error saving the Resolution Form");
    } finally {
      setSaving(false);
    }
  };

  const startAddOption = () => {
    setIsAddingOption(true);

    setEditingOption({
      type: "list_option",
      title: {
        en_US: "",
      },
      should_be_aggregated: false,
      items: [],
    });


    // null means this is NEW, not an existing option.
    setEditingOptionIndex(null);
  };
  const saveNewOption = () => {
    const newLabel = editingOption?.title?.en_US?.trim();

    if (
      !newLabel ||
      !editingField ||
      editingIndex === null
    ) {
      return;
    }

    const newOption = {
      ...editingOption,
      title: {
        ...(editingOption?.title || {}),
        en_US: newLabel,
      },
    };

    // Build the updated FIELD working copy.
    const updatedField = {
      ...editingField,
      items: [
        ...(Array.isArray(editingField?.items)
          ? editingField.items
          : []),
        newOption,
      ],
    };

    // Keep the open field editor current.
    setEditingField(updatedField);

    // IMPORTANT:
    // Also put the updated field into the working form fields[].
    // Save Form sends fields[] to the backend.
    setFields(
      fields.map((field, index) =>
        index === editingIndex
          ? updatedField
          : field
      )
    );

    // Working form now differs from MongoDB.
    setHasUnsavedChanges(true);

    // Close the new-option editor.
    setIsAddingOption(false);
    setEditingOption(null);
  };

  const removeOption = (optionIndex) => {
    if (!editingField || editingIndex === null) return;

    const updatedOptions = editingField.items.filter(
      (_, index) => index !== optionIndex
    );

    const updatedField = {
      ...editingField,
      items: updatedOptions,
    };

    // Keep the open field editor current.
    setEditingField(updatedField);

    // Save Form sends fields[] to the backend,
    // so update the working form too.
    setFields(
      fields.map((field, index) =>
        index === editingIndex
          ? updatedField
          : field
      )
    );

    setHasUnsavedChanges(true);

    // Close the confirmation.
    setRemovingOptionIndex(null);
  };
  // ------------------------------------------------------------
  // NEW FIELD
  // ------------------------------------------------------------

  const saveNewField = () => {
    const newLabel = newField?.title?.en_US?.trim();

    if (!newLabel || !newField?.type) return;

    const fieldToAdd = {
      ...newField,
      title: {
        ...(newField?.title || {}),
        en_US: newLabel,
      },
      items: Array.isArray(newField?.items)
        ? newField.items
        : [],
    };

    // Legacy-compatible behavior:
    // only store `required` when the field is actually required.
    // Missing `required` means false.
    if (newField?.required === true) {
      fieldToAdd.required = true;
    } else {
      delete fieldToAdd.required;
    }

    // Statistic name: Only store statistic_name when one was entered.
    const statisticName = newField?.statistic_name?.trim();

    if (statisticName) {
      fieldToAdd.statistic_name = statisticName;
    } else {
      delete fieldToAdd.statistic_name;
    }
    // Add the new field to the working Resolution Form.
    setFields([
      ...fields,
      fieldToAdd,
    ]);

    // The working form now differs from MongoDB.
    setHasUnsavedChanges(true);

    // Close/reset the new-field editor.
    setIsAddingField(false);
    setNewField(null);
  };

  const cancelNewField = () => {
    setIsAddingField(false);
    setNewField(null);
  };
  const removeField = (fieldIndex) => {
    // Remove the selected field from the working Resolution Form.
    const updatedFields = fields.filter(
      (_, index) => index !== fieldIndex
    );

    setFields(updatedFields);

    // The working form now differs from MongoDB.
    setHasUnsavedChanges(true);

    // Close the remove confirmation.
    setRemovingFieldIndex(null);

    // If somehow this was also the field being edited, close that editor.
    if (editingIndex === fieldIndex) {
      setEditingIndex(null);
      setEditingField(null);
    }
  };
  // ------------------------------------------------------------
  // MOVE FIELD UP / DOWN
  // ------------------------------------------------------------

  const moveField = (fieldIndex, direction) => {
    const newIndex = fieldIndex + direction;

    // Do nothing if we're already at the top or bottom.
    if (newIndex < 0 || newIndex >= fields.length) return;

    const updatedFields = [...fields];

    // Swap the two fields.
    [updatedFields[fieldIndex], updatedFields[newIndex]] = [
      updatedFields[newIndex],
      updatedFields[fieldIndex],
    ];

    // Update the working Resolution Form.
    setFields(updatedFields);

    // The form now differs from MongoDB.
    setHasUnsavedChanges(true);

    // If a remove confirmation was open on the moved field,
    // keep that confirmation attached to the same field.
    if (removingFieldIndex === fieldIndex) {
      setRemovingFieldIndex(newIndex);
    }
  };
  // ------------------------------------------------------------
  // MOVE OPTION UP / DOWN
  // ------------------------------------------------------------

  const moveOption = (fieldIndex, optionIndex, direction) => {
    if (!editingField || fieldIndex === null) return;

    const newOptionIndex = optionIndex + direction;

    const updatedItems = [
      ...(Array.isArray(editingField.items)
        ? editingField.items
        : []),
    ];

    // Stop if already at the top or bottom.
    if (
      newOptionIndex < 0 ||
      newOptionIndex >= updatedItems.length
    ) {
      return;
    }

    // Swap the two options.
    [updatedItems[optionIndex], updatedItems[newOptionIndex]] = [
      updatedItems[newOptionIndex],
      updatedItems[optionIndex],
    ];

    const updatedField = {
      ...editingField,
      items: updatedItems,
    };

    // Update what the user is currently looking at.
    setEditingField(updatedField);

    // Update the working form that Save Form will send to Mongo.
    setFields(
      fields.map((field, index) =>
        index === fieldIndex
          ? updatedField
          : field
      )
    );

    // The working form now differs from Mongo.
    setHasUnsavedChanges(true);

    // Keep remove confirmation attached to the same option.
    if (removingOptionIndex === optionIndex) {
      setRemovingOptionIndex(newOptionIndex);
    }

    // Keep option editor attached to the same option.
    if (editingOptionIndex === optionIndex) {
      setEditingOptionIndex(newOptionIndex);
    }
  };

  // START OF JSX WEB CODE TO RENDER PAGE //
  return (
    <div style={{ padding: 12, maxWidth: 800 }}>
      <div className="cfg-title">Resolution Form</div>
      <div className="cfg-subtitle">
        Design the form used by responders and the dispatch console to complete
        and resolve incidents.
      </div>

      {/* ------------------------------------------------------------ */}
      {/* FORM TOOLBAR                                                 */}
      {/* ------------------------------------------------------------ */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <strong>Form Fields</strong>

        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
          }}
        >
          {/* ADD NEW FIELD */}
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => {
              setNewField({
                title: {
                  en_US: "",
                },
                type: "text",
                required: false,
                // Used for Selection List reporting.
                statistic_name: "",
                // Placeholder shown before a Selection List choice is made.
                selection_prompt: "",

                // Only applies to Number / Integer fields.
                should_be_aggregated: false,

                items: [],
              });

              setIsAddingField(true);
            }}
            style={{
              whiteSpace: "nowrap",
            }}
          >
            + Add Field
          </button>

          {/* ONLY SHOW WHEN FORM HAS UNSAVED CHANGES */}
          {hasUnsavedChanges && (
            <button
              type="button"
              className="btn btn-success"
              onClick={saveForm}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Form"}
            </button>
          )}

          {/* RELOAD FORM FROM DATABASE */}
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={load}
            disabled={loading}
          >
            {loading ? "Loading…" : "Reload"}
          </button>
        </div>
      </div>
      {/* ------------------------------------------------------------ */}
      {/* NEW FIELD EDITOR                                             */}
      {/* ------------------------------------------------------------ */}

      {isAddingField && newField && (
        <div
          style={{
            marginTop: 12,
            marginBottom: 14,
            padding: "16px",
            border: "1px solid #ddd",
            borderRadius: 10,
            background: "#f8f9fa",
          }}
        >
          {/* FIELD NAME */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 14,
            }}
          >
            <input
              type="text"
              className="form-control"
              placeholder="Enter field name..."
              value={newField?.title?.en_US || ""}
              onChange={(e) =>
                setNewField({
                  ...newField,
                  title: {
                    ...(newField?.title || {}),
                    en_US: e.target.value,
                  },
                })
              }
              style={{
                border: "2px solid #0d6efd",
                background: "#fffdf2",
                boxShadow: "0 0 0 2px rgba(13,110,253,.12)",
              }}
            />
          </div>

          {/* FIELD TYPE */}
          <div>
            <label
              style={{
                display: "block",
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              Field Type
            </label>

            <select
              className="form-control"
              value={newField?.type || "text"}
              onChange={(e) => {
                const nextType = e.target.value;

                setNewField({
                  ...newField,
                  type: nextType,

                  // Aggregation only applies to numeric field types.
                  should_be_aggregated:
                    nextType === "number" || nextType === "integer"
                      ? newField?.should_be_aggregated === true
                      : false,
                });
              }}
            >
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="integer">Integer</option>
              <option value="bool">Checkbox</option>
              <option value="photo">Photo</option>
              <option value="list">Selection List</option>
            </select>
            {/* ------------------------------------------------------------ */}
            {/* NUMERIC FIELD ANALYTICS                                      */}
            {/* Only show for Number and Integer fields.                     */}
            {/* ------------------------------------------------------------ */}

            {(newField?.type === "number" ||
              newField?.type === "integer") && (
                <div
                  style={{
                    marginTop: 12,
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      margin: 0,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={newField?.should_be_aggregated === true}
                      onChange={(e) =>
                        setNewField({
                          ...newField,
                          should_be_aggregated: e.target.checked,
                        })
                      }
                    />

                    <span>Include in analytics</span>
                  </label>

                  <div
                    style={{
                      marginTop: 4,
                      marginLeft: 24,
                      fontSize: "0.82rem",
                      color: "#666",
                    }}
                  >
                    Use for numeric values that should be included in reports and statistics.
                  </div>
                </div>
              )}
          </div>

          {/* ------------------------------------------------------------ */}
          {/* REQUIRED FIELD                                               */}
          {/* ------------------------------------------------------------ */}

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 12,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={newField?.required === true}
              onChange={(e) =>
                setNewField({
                  ...newField,
                  required: e.target.checked,
                })
              }
            />

            <span>Required</span>
          </label>
          {/* ------------------------------------------------------------ */}
          {/* STATISTICS NAME - SELECTION LIST                             */}
          {/* ------------------------------------------------------------ */}

          {newField?.type === "list" && (
            <div
              style={{
                marginTop: 12,
              }}
            >
              <input
                type="text"
                className="form-control"
                placeholder="Statistics name..."
                value={newField?.statistic_name || ""}
                onChange={(e) =>
                  setNewField({
                    ...newField,
                    statistic_name: e.target.value,
                  })
                }
              />

              <div
                style={{
                  marginTop: 4,
                  fontSize: "0.82rem",
                  color: "#666",
                }}
              >
                Used in reports and statistics (up to 4 words).
              </div>
            </div>
          )}
 {/* ------------------------------------------------------------ */}
          {/* SELECTION PROMPT - NEW SELECTION LIST                        */}
          {/* Text shown inside the dropdown before an option is chosen.  */}
          {/* ------------------------------------------------------------ */}
          {newField?.type === "list" && (
            <div
              style={{
                marginTop: 12,
              }}
            >
              <label
                style={{
                  display: "block",
                  fontWeight: 700,
                  marginBottom: 6,
                }}
              >
                Selection Prompt
              </label>

              <input
                type="text"
                className="form-control"
                placeholder="Example: Select a report source"
                value={newField?.selection_prompt || ""}
                onChange={(e) =>
                  setNewField({
                    ...newField,
                    selection_prompt: e.target.value,
                  })
                }
              />

              <div
                style={{
                  marginTop: 4,
                  fontSize: "0.82rem",
                  color: "#666",
                }}
              >
                Shown inside the selection box before an option is chosen.
              </div>
            </div>
          )}

          {/* ------------------------------------------------------------ */}
          {/* NEW FIELD ACTIONS BUTTONS                                    */}
          {/* ------------------------------------------------------------ */}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 14,
            }}
          >
            <button
              type="button"
              className="btn btn-primary"
              onClick={saveNewField}
              disabled={!newField?.title?.en_US?.trim()}
            >
              Add Field
            </button>

            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={cancelNewField}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="cfg-list">
        {fields.length === 0 ? (
          <div style={{ padding: 12 }}>
            <em>No Resolution Form fields are configured.</em>
          </div>
        ) : (
          fields.map((field, index) => (
            <React.Fragment key={`${field?._id || "new-field"}-${index}`}>


              {field?.type !== "list" &&
                index > 0 &&
                fields[index - 1]?.type === "list" && (
                  <div
                    style={{
                      background: "#fff3cd",
                      border: "1px solid #ffe69c",
                      borderRadius: 8,
                      padding: "10px 14px",
                      marginTop: 8,
                      marginBottom: 8,
                      fontWeight: 700,
                      color: "#664d03",
                    }}
                  >
                    Responder Actions
                  </div>
                )}


              {/* NORMAL FIELD ROW - HIDE WHILE THIS FIELD IS BEING EDITED */}
              {editingIndex !== index && (
                <div
                  className="cfg-list-item"
                  style={{
                    width: "100%",

                    background:
                      field?.type === "list"
                        ? "#f4f8ff"
                        : "#fff",

                    border:
                      field?.type === "list"
                        ? "1px solid #bcd6ff"
                        : "1px solid transparent",

                    borderRadius:
                      field?.type === "list"
                        ? 10
                        : 0,

                    boxShadow:
                      field?.type === "list"
                        ? "0 2px 6px rgba(0,0,0,.06)"
                        : "none",

                    padding:
                      field?.type === "list"
                        ? "14px 16px"
                        : undefined,
                  }}
                >
                  {/* ------------------------------------------------------------ */}
                  {/* FIELD TOP ROW - LABEL + ACTION BUTTONS                       */}
                  {/* ------------------------------------------------------------ */}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      width: "100%",
                      gap: 16,
                    }}
                  >
                    {/* LEFT SIDE - Hide while editing this field */}
                    {/*THIS RENDERS THAT INITIAL LIST OF FIELDS */}

                    <div>
                      <div style={{ fontWeight: 700 }}>
                        {field?.type === "list" && "📋 "}
                        {field?.title?.en_US || "Untitled field"}
                      </div>

                      <div
                        style={{
                          fontSize: "0.85rem",
                          color: "#666",
                          textTransform: "capitalize",
                        }}
                      >
                        {field?.type || "Unknown type"}

                        {field?.type === "list" && (
                          <>
                            {" "}
                            · {Array.isArray(field?.items) ? field.items.length : 0} options
                          </>
                        )}
                        {field?.type === "list" && field?.statistic_name && (
                          <div
                            style={{
                              fontSize: "0.82rem",
                              color: "#666",
                              marginTop: 2,
                            }}
                          >
                            Statistics Name: {field.statistic_name}
                          </div>
                        )}

                        {field?.type === "number" &&
                          field?.should_be_aggregated === true && (
                            <> · Included in analytics</>
                          )}
                      </div>
                    </div>


                    {/* ------------------------------------------------------------ */}
                    {/* FIELD ACTION BUTTONS                                         */}
                    {/* ------------------------------------------------------------ */}

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {/* MOVE FIELD UP */}
                      <button
                        type="button"
                        className="cfg-icon-btn"
                        title="Move field up"
                        onClick={() => moveField(index, -1)}
                        disabled={index === 0}
                      >
                        <span className="material-icons cfg-icon cfg-move-icon">
                          keyboard_arrow_up
                        </span>
                      </button>

                      {/* MOVE FIELD DOWN */}
                      <button
                        type="button"
                        className="cfg-icon-btn"
                        title="Move field down"
                        onClick={() => moveField(index, 1)}
                        disabled={index === fields.length - 1}
                      >
                        <span className="material-icons cfg-icon cfg-move-icon">
                          keyboard_arrow_down
                        </span>
                      </button>
                      {/* EDIT FIELD BUTTON - pencil */}
                      <button
                        type="button"
                        className="cfg-icon-btn"
                        title="Edit field"
                        onClick={() => startFieldEdit(field, index)}
                      >
                        <span className="material-icons cfg-icon cfg-edit-icon">
                          edit
                        </span>
                      </button>

                      {/* REMOVE FIELD BUTTON - red circle */}
                      <button
                        type="button"
                        className="cfg-icon-btn"
                        title="Remove field"
                        onClick={() => setRemovingFieldIndex(index)}
                      >
                        <span className="material-icons cfg-icon cfg-remove-icon">
                          block
                        </span>
                      </button>
                    </div>


                  </div>
                  {/* END FIELD TOP ROW */}

                  {/* ------------------------------------------------------------ */}
                  {/* REMOVE FIELD CONFIRMATION                                    */}
                  {/* ------------------------------------------------------------ */}

                  {removingFieldIndex === index && (
                    <div
                      style={{
                        width: "100%",
                        marginTop: 10,
                        padding: "12px 14px",
                        background: "#fff8f8",
                        border: "1px solid #f1c2c7",
                        borderRadius: 8,
                      }}
                    >
                      <div
                        style={{
                          marginBottom: 10,
                          fontWeight: 600,
                        }}
                      >
                        Remove "{field?.title?.en_US || "this field"}" from this Resolution Form?
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removeField(index)}
                        >
                          Remove
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setRemovingFieldIndex(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}


                </div>
              )}
              {/* EXPANDED FIELD EDITOR */}
              {editingIndex === index && editingField && (
                <div
                  style={{
                    padding: "18px",
                    background: "#f8f9fa",
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  {/* FIELD NAME */}
                  {/* ------------------------------------------------------------ */}
                  {/* EDIT FIELD NAME                                              */}
                  {/* ------------------------------------------------------------ */}

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 18,
                    }}
                  >
                    {/* FIELD NAME INPUT */}
                    <input
                      id={`field-name-${index}`}
                      type="text"
                      className="form-control"
                      value={editingField?.title?.en_US || ""}
                      onChange={(e) =>
                        setEditingField({
                          ...editingField,
                          title: {
                            ...(editingField?.title || {}),
                            en_US: e.target.value,
                          },
                        })
                      }
                      style={{
                        border: "2px solid #0d6efd",
                        background: "#fffdf2",
                        boxShadow: "0 0 0 2px rgba(13,110,253,.12)",
                      }}
                    />

                    {/* ACCEPT FIELD NAME CHANGE */}
                    <button
                      type="button"
                      className="cfg-icon-btn"
                      title="Accept field name change"
                      onClick={saveFieldEdit}
                      disabled={!editingField?.title?.en_US?.trim()}
                    >
                      <span
                        className="material-icons cfg-icon"
                        style={{ color: "#198754" }}
                      >
                        check
                      </span>
                    </button>

                    {/* CANCEL FIELD NAME CHANGE */}
                    <button
                      type="button"
                      className="cfg-icon-btn"
                      title="Discard field name change"
                      onClick={cancelFieldEdit}
                    >
                      <span className="material-icons cfg-icon cfg-close-icon">
                        close
                      </span>
                    </button>
                  </div>

                  {/* ------------------------------------------------------------ */}
                  {/* FIELD TYPE (Locked for existing fields)                      */}
                  {/* ------------------------------------------------------------ */}

                  <div style={{ marginBottom: 20 }}>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 6,
                      }}
                    >

                      <span style={{ fontWeight: 700 }}>
                        Field Type:
                      </span>

                      <span style={{ fontWeight: 500 }}>
                        {editingField?.type === "bool"
                          ? "Checkbox"
                          : editingField?.type === "list"
                            ? "Selection List"
                            : editingField?.type === "photo"
                              ? "Photo"
                              : editingField?.type === "number"
                                ? "Number"
                                : editingField?.type === "integer"
                                  ? "Integer"
                                  : editingField?.type === "text"
                                    ? "Text"
                                    : editingField?.type || "Unknown"}
                      </span>

                      <span
                        style={{
                          color: "#dc3545",
                          fontSize: "0.85rem",
                          fontWeight: 500,
                        }}
                      >
                        🔒 Locked for existing fields
                      </span>
                    </div>
                  </div>
                  {/* END FIELD TYPE */}
                  {/* ------------------------------------------------------------ */}
                  {/* REQUIRED FIELD - EXISTING FIELD                              */}
                  {/* ------------------------------------------------------------ */}

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 16,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={editingField?.required === true}
                      onChange={(e) =>
                        setEditingField({
                          ...editingField,
                          required: e.target.checked,
                        })
                      }
                    />

                    <span>Required</span>
                  </label>

                  {/* ------------------------------------------------------------ */}
                  {/* NUMERIC FIELD ANALYTICS - EXISTING FIELD                     */}
                  {/* Only show for Number and Integer fields.                     */}
                  {/* ------------------------------------------------------------ */}

                  {(editingField?.type === "number" ||
                    editingField?.type === "integer") && (
                      <div
                        style={{
                          marginBottom: 20,
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            margin: 0,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={editingField?.should_be_aggregated === true}
                            onChange={(e) =>
                              setEditingField({
                                ...editingField,
                                should_be_aggregated: e.target.checked,
                              })
                            }
                          />

                          <span>Include in analytics</span>
                        </label>

                        <div
                          style={{
                            marginTop: 4,
                            marginLeft: 24,
                            fontSize: "0.82rem",
                            color: "#666",
                          }}
                        >
                          Use for numeric values that should be included in reports and statistics.
                        </div>
                      </div>
                    )}
                  {/* ------------------------------------------------------------ */}
                  {/* STATISTICS NAME - EXISTING SELECTION LIST                    */}
                  {/* ------------------------------------------------------------ */}

                  {editingField?.type === "list" && (
                    <div
                      style={{
                        marginBottom: 20,
                      }}
                    >
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Statistics name..."
                        value={editingField?.statistic_name || ""}
                        onChange={(e) =>
                          setEditingField({
                            ...editingField,
                            statistic_name: e.target.value,
                          })
                        }
                      />

                      <div
                        style={{
                          marginTop: 4,
                          fontSize: "0.82rem",
                          color: "#666",
                        }}
                      >
                        Used in reports and statistics (up to 4 words).
                      </div>
                    </div>
                  )}
                  {/* ------------------------------------------------------------ */}
                  {/* SELECTION PROMPT - EXISTING SELECTION LIST                   */}
                  {/* Text shown inside the dropdown before an option is chosen.  */}
                  {/* ------------------------------------------------------------ */}
                  {editingField?.type === "list" && (
                    <div
                      style={{
                        marginBottom: 20,
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          fontWeight: 700,
                          marginBottom: 6,
                        }}
                      >
                        Selection Prompt
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        placeholder="Example: Select a report source"
                        value={editingField?.selection_prompt || ""}
                        onChange={(e) =>
                          setEditingField({
                            ...editingField,
                            selection_prompt: e.target.value,
                          })
                        }
                      />

                      <div
                        style={{
                          marginTop: 4,
                          fontSize: "0.82rem",
                          color: "#666",
                        }}
                      >
                        Shown inside the selection box before an option is chosen.
                      </div>
                    </div>
                  )}

                  {/* ------------------------------------------------------------ */}
                  {/* CURRENT LIST OPTIONS - ONLY SHOW FOR LIST FIELDS                     */}
                  {/* ------------------------------------------------------------ */}

                  {editingField?.type === "list" && (
                    <div style={{ marginBottom: 20 }}>
                      {/* LIST HEADER */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 8,
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>
                          Current List Options
                        </div>

                        {/* ADD OPTION BUTTON */}
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={startAddOption}
                          disabled={isAddingOption}
                          style={{
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            minWidth: 140,
                            fontWeight: 500,
                          }}
                        >
                          + Add Option
                        </button>

                      </div>
                      {/* ------------------------------------------------------------ */}
                      {/* NEW OPTION EDITOR                                             */}
                      {/* ------------------------------------------------------------ */}

                      {isAddingOption && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "10px 0",
                            borderBottom: "1px solid #e5e5e5",
                          }}
                        >
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter new option..."
                            value={editingOption?.title?.en_US || ""}
                            onChange={(e) =>
                              setEditingOption({
                                ...editingOption,
                                title: {
                                  ...(editingOption?.title || {}),
                                  en_US: e.target.value,
                                },
                              })
                            }
                            style={{
                              border: "2px solid #0d6efd",
                              background: "#fffdf2",
                              boxShadow: "0 0 0 2px rgba(13,110,253,.12)",
                            }}
                          />

                          {/* SAVE / ACCEPT NEW OPTION */}
                          <button
                            type="button"
                            className="cfg-icon-btn"
                            title="Add option"
                            onClick={saveNewOption}
                            disabled={!editingOption?.title?.en_US?.trim()}
                          >
                            <span
                              className="material-icons cfg-icon"
                              style={{ color: "#198754" }}
                            >
                              check
                            </span>
                          </button>

                          {/* CANCEL */}
                          <button
                            type="button"
                            className="cfg-icon-btn"
                            title="Cancel"
                            onClick={() => {
                              setIsAddingOption(false);
                              setEditingOption(null);
                            }}
                          >
                            <span className="material-icons cfg-icon cfg-close-icon">
                              close
                            </span>
                          </button>
                        </div>
                      )}
                      {Array.isArray(editingField?.items) &&
                        editingField.items.length > 0 ? (


                        <div
                          style={{
                            border: "1px solid #ddd",
                            borderRadius: 10,
                            background: "#fff",
                            padding: "0 14px",
                          }}
                        >


                          {editingField.items.map((option, optionIndex) => (

                            <div
                              key={`${option?._id || "option"}-${optionIndex}`}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 12,
                                padding: "10px 0",
                                borderBottom:
                                  optionIndex === editingField.items.length - 1
                                    ? "none"
                                    : "1px solid #e5e5e5",
                              }}
                            >
                              {/* ------------------------------------------------------------ */}
                              {/* OPTION IS CURRENTLY BEING EDITED                             */}
                              {/* ------------------------------------------------------------ */}

                              {editingOptionIndex === optionIndex && editingOption ? (
                                <>
                                  {/* EDIT OPTION TEXT */}
                                  <input
                                    type="text"
                                    className="form-control"
                                    style={{
                                      border: "2px solid #0d6efd",
                                      background: "#fffdf2",
                                      boxShadow: "0 0 0 2px rgba(13,110,253,.12)",
                                    }}
                                    value={editingOption?.title?.en_US || ""}
                                    onChange={(e) =>
                                      setEditingOption({
                                        ...editingOption,
                                        title: {
                                          ...(editingOption?.title || {}),
                                          en_US: e.target.value,
                                        },
                                      })
                                    }
                                  />

                                  {/* SAVE OPTION */}
                                  <button
                                    type="button"
                                    className="cfg-icon-btn"
                                    title="Save option"
                                    onClick={saveOptionEdit}
                                    disabled={!editingOption?.title?.en_US?.trim()}
                                  >
                                    <span
                                      className="material-icons cfg-icon"
                                      style={{ color: "#055f35" }}
                                    >
                                      check
                                    </span>
                                  </button>

                                  {/* CANCEL OPTION EDIT */}
                                  <button
                                    type="button"
                                    className="cfg-icon-btn"
                                    title="Cancel option edit"
                                    onClick={cancelOptionEdit}
                                  >
                                    <span className="material-icons cfg-icon cfg-close-icon">
                                      close
                                    </span>
                                  </button>
                                </>
                              ) : (
                                <>
                                  {/* ------------------------------------------------------------ */}
                                  {/* NORMAL OPTION ROW                                           */}
                                  {/* ------------------------------------------------------------ */}

                                  <div
                                    style={{
                                      width: "100%",
                                    }}
                                  >
                                    {/* ------------------------------------------------------------ */}
                                    {/* OPTION TOP ROW - LABEL + ACTION BUTTONS                     */}
                                    {/* ------------------------------------------------------------ */}

                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        width: "100%",
                                      }}
                                    >
                                      {/* OPTION LABEL */}
                                      <div>
                                        {option?.title?.en_US || "Untitled option"}
                                      </div>

                                      {/* ------------------------------------------------------------ */}
                                      {/* OPTION ACTION BUTTONS                                      */}
                                      {/* ------------------------------------------------------------ */}
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                        }}
                                      >
                                        {/* MOVE OPTION UP */}
                                        <button
                                          type="button"
                                          className="cfg-icon-btn"
                                          title="Move option up"
                                          onClick={() => moveOption(editingIndex, optionIndex, -1)}
                                          disabled={optionIndex === 0}
                                        >
                                          <span className="material-icons cfg-icon cfg-move-icon">
                                            keyboard_arrow_up
                                          </span>
                                        </button>

                                        {/* MOVE OPTION DOWN */}
                                        <button
                                          type="button"
                                          className="cfg-icon-btn"
                                          title="Move option down"
                                          onClick={() => moveOption(editingIndex, optionIndex, 1)}
                                          disabled={optionIndex === editingField.items.length - 1}
                                        >
                                          <span className="material-icons cfg-icon cfg-move-icon">
                                            keyboard_arrow_down
                                          </span>
                                        </button>
                                        {/* EDIT OPTION BUTTON - pencil */}
                                        <button
                                          type="button"
                                          className="cfg-icon-btn"
                                          title="Edit option"
                                          onClick={() => startOptionEdit(option, optionIndex)}
                                        >
                                          <span className="material-icons cfg-icon cfg-edit-icon">
                                            edit
                                          </span>
                                        </button>

                                        {/* REMOVE OPTION BUTTON - circle */}
                                        <button
                                          type="button"
                                          className="cfg-icon-btn"
                                          title="Remove option"
                                          onClick={() => setRemovingOptionIndex(optionIndex)}
                                        >
                                          <span className="material-icons cfg-icon cfg-remove-icon">
                                            block
                                          </span>
                                        </button>
                                      </div>
                                    </div>

                                    {/* ------------------------------------------------------------ */}
                                    {/* REMOVE OPTION CONFIRMATION                                  */}
                                    {/* ------------------------------------------------------------ */}

                                    {removingOptionIndex === optionIndex && (
                                      <div
                                        style={{
                                          width: "100%",
                                          marginTop: 10,
                                          padding: "12px 14px",
                                          background: "#fff8f8",
                                          border: "1px solid #f1c2c7",
                                          borderRadius: 8,
                                        }}
                                      >
                                        <div
                                          style={{
                                            marginBottom: 10,
                                            fontWeight: 600,
                                          }}
                                        >
                                          Remove "{option?.title?.en_US || "this option"}" from this selection list?
                                        </div>

                                        <div
                                          style={{
                                            display: "flex",
                                            gap: 10,
                                          }}
                                        >
                                          <button
                                            type="button"
                                            className="btn btn-outline-danger"
                                            onClick={() => removeOption(optionIndex)}
                                          >
                                            Remove
                                          </button>

                                          <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => setRemovingOptionIndex(null)}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          ))}

                        </div>

                      ) : (
                        <div style={{ color: "#666" }}>
                          No options configured.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          ))
        )
        }
      </div>

    </div>
  );
};

export default ResolutionFormEditor;
